import React, { FC, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { OnboardingCommitCounts } from '@intelligent-play-v2/domain';

import { getPromotionPreview, getPromotionStatus, postPromotion } from '~/api';
import { Button, ButtonType } from '~/components/button';
import { Loading } from '~/components/loading';
import { Modal } from '~/components/modals';

interface PromoteFacilityButtonProps {
  facilityId: number;
  facilityName: string;
}

const pluralise = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

const describeCounts = (counts: OnboardingCommitCounts): string =>
  [
    '1 facility',
    pluralise(counts.pitches, 'pitch', 'pitches'),
    pluralise(counts.pitchSportTypes, 'sport type', 'sport types'),
    pluralise(counts.devices, 'device', 'devices'),
    pluralise(counts.alertThresholds, 'alert threshold', 'alert thresholds'),
    pluralise(
      counts.maintenanceSchedules,
      'maintenance schedule',
      'maintenance schedules'
    ),
    pluralise(
      counts.snapshotSchedules,
      'snapshot schedule',
      'snapshot schedules'
    ),
  ].join(', ');

/**
 * "Promote to production", for a facility that has been validated on staging.
 *
 * Renders nothing unless the API says this deployment has a promotion target, which is
 * how production never shows the button. Opens a preview first: what will be copied and
 * anything in the way, read from the target at that moment, so the reviewer confirms a
 * specific outcome rather than an intention.
 */
export const PromoteFacilityButton: FC<PromoteFacilityButtonProps> = ({
  facilityId,
  facilityName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // what the server last said when it refused; replaces the preview's list
  const [refusal, setRefusal] = useState<string[]>([]);

  const { data: status } = useQuery('promotionStatus', getPromotionStatus, {
    staleTime: Infinity,
  });

  const {
    data: preview,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
    error: previewError,
    refetch: refetchPreview,
  } = useQuery(
    ['promotionPreview', facilityId],
    async () => getPromotionPreview(facilityId),
    { enabled: isOpen, staleTime: 0, cacheTime: 0, retry: false }
  );

  const { mutate: promote, isLoading: isPromoting } = useMutation(
    postPromotion,
    {
      onSuccess: response => {
        toast.success(
          `${facilityName} promoted to ${response.targetName}: ${describeCounts(
            response.counts
          )}.`,
          { duration: 10000 }
        );
        setIsOpen(false);
      },
      onError: (
        error: AxiosError<{ message?: string; details?: string[] }>
      ) => {
        const data = error.response?.data;
        toast.error(data?.message ?? 'Promotion failed. Nothing was written.');
        // a 409 names what is in the way; show it where the preview was
        if (data?.details && data.details.length > 0) {
          setRefusal(data.details);
        }
      },
    }
  );

  if (!status?.enabled) {
    return null;
  }

  const targetName = status.targetName;
  const conflicts = refusal.length > 0 ? refusal : preview?.conflicts ?? [];
  const canPromote =
    !!preview && conflicts.length === 0 && !isPromoting && !isPreviewLoading;

  const open = (): void => {
    setRefusal([]);
    setIsOpen(true);
    refetchPreview();
  };

  return (
    <>
      <Button
        text={`Promote to ${targetName}`}
        type={ButtonType.Primary}
        onClick={open}
      />
      <Modal
        showModal={isOpen}
        setShowModal={setIsOpen}
        title={`Promote to ${targetName}`}
        subtitle={facilityName}
      >
        <div className="max-w-xl space-y-4 text-base">
          <p className="text-primary-500">
            Copies this facility&apos;s configuration to {targetName} exactly as
            it is here now — the facility, its pitches, sensors, alert
            thresholds, maintenance and snapshot schedules. No detections, usage
            or snapshot data goes with it, and nothing here changes.
          </p>

          {isPreviewLoading && <Loading />}

          {isPreviewError && (
            <p className="text-red-dark">
              {(previewError as AxiosError<{ message?: string }>)?.response
                ?.data?.message ??
                `Could not read ${targetName} to check this facility. Please try again.`}
            </p>
          )}

          {preview && (
            <div className="p-4 border rounded border-primary-250 bg-primary-100">
              <p>
                <span className="font-semibold">Writes: </span>
                {describeCounts(preview.counts)}.
              </p>
              {preview.skippedCustomSnapshotSchedules > 0 && (
                <p className="mt-2 text-sm text-primary-500">
                  {pluralise(
                    preview.skippedCustomSnapshotSchedules,
                    'snapshot schedule belongs',
                    'snapshot schedules belong'
                  )}{' '}
                  to a custom date range set up here and will not be copied.
                </p>
              )}
            </div>
          )}

          {conflicts.length > 0 && (
            <div className="p-4 border rounded border-red bg-red-lightest text-red-dark">
              <p className="font-semibold">
                {targetName} will refuse this facility as it stands:
              </p>
              <ul className="mt-2 space-y-1 text-sm list-disc list-inside">
                {conflicts.map(conflict => (
                  <li key={conflict}>{conflict}</li>
                ))}
              </ul>
            </div>
          )}

          {preview && conflicts.length === 0 && (
            <p className="text-sm text-primary-500">
              {targetName}&apos;s facility list is cached for up to 15 minutes,
              so the facility may take that long to appear there.
            </p>
          )}

          <div className="flex justify-end space-x-3">
            <div className="w-32">
              <Button
                text="Cancel"
                type={ButtonType.Outline}
                onClick={() => setIsOpen(false)}
                disabled={isPromoting}
              />
            </div>
            <div className="w-48">
              <Button
                text={`Promote to ${targetName}`}
                type={ButtonType.Primary}
                onClick={() => promote(facilityId)}
                disabled={!canPromote}
                isLoading={isPromoting}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
